/**
 * toUserMessage — wraps an exception's message in a per-surface friendly
 * fallback when the message looks like raw exception text.
 *
 * Two failure modes the wrapper closes (#1214):
 *
 *   1. The catch site reaches for `err.message` and the underlying error is
 *      a `TypeError` / `ReferenceError` / `ENOENT` / a fetch failure with a
 *      stack-trace fragment in the message. Operators see developer slop
 *      and can't act on it.
 *
 *   2. The backend's own validation responses ("Workspace not found", "Token
 *      revoked") are already plain English. Wrapping those in a generic
 *      fallback would *hide* actionable detail. The heuristic deliberately
 *      lets those through unchanged.
 *
 * The split is heuristic, not exact — a regex pass for JS/Node identifiers
 * at the start of the message + stack-frame fragments. False negatives
 * (a backend message that happens to mention "TypeError" in prose) are
 * theoretical but tolerable; the cost is rendering one already-readable
 * message. False positives are the bug we're solving and the conservative
 * call.
 *
 * Co-located with the toast primitive — every call site that uses
 * `toUserMessage` also uses `useToast`. Splitting into a separate `errors/`
 * module would be sprawl.
 */

// Identifiers JS / Node throws under that read as developer text in a toast.
// Listed individually rather than `.*Error` so we don't false-positive on
// backend messages like "Error loading workspaces" (operator-visible English
// that happens to contain the word).
const RAW_PREFIX_PATTERNS = [
  /^TypeError\b/,
  /^ReferenceError\b/,
  /^SyntaxError\b/,
  /^RangeError\b/,
  /^URIError\b/,
  /^EvalError\b/,
  /^ENOENT\b/,
  /^EACCES\b/,
  /^ECONNREFUSED\b/,
  /^ETIMEDOUT\b/,
  /^EPIPE\b/,
  /^ENOTFOUND\b/,
  // Common fetch failures — every browser names these differently and they
  // all read as developer text in a toast.
  /^Failed to fetch\b/i,
  /^NetworkError\b/,
  /^Load failed$/i,
  /^The user aborted a request\b/i,
  // Generic identifier-followed-by-colon shape ("Foo: bar"). Matches any
  // PascalCase identifier ending in Error / Exception, which catches custom
  // error classes the JS runtime or libraries throw without listing each one.
  /^[A-Z][A-Za-z0-9_]*(?:Error|Exception):/,
]

// Stack-trace fragments. These appear when an error message accidentally
// includes the toString of an Error with its stack.
const STACK_FRAGMENT_PATTERNS = [
  /\bat [A-Za-z_$][\w$.]* \(/, // "at functionName ("
  /\bat\s+\S+:\d+:\d+/, // "at someFile.js:12:34"
  /:\d+:\d+\)/, // ":12:34)" — end of a stack frame
]

/**
 * Returns true if `msg` looks like raw exception text — not something an
 * operator should be reading. Used internally by `toUserMessage`; exported
 * for the test harness.
 */
export function looksLikeRawException(msg: string): boolean {
  if (!msg) return true
  const trimmed = msg.trim()
  if (!trimmed) return true
  for (const re of RAW_PREFIX_PATTERNS) {
    if (re.test(trimmed)) return true
  }
  for (const re of STACK_FRAGMENT_PATTERNS) {
    if (re.test(trimmed)) return true
  }
  return false
}

// Built-in Error subclass names whose .message never reads well in a toast.
// We check `err.name` separately from the message because the runtime
// doesn't prefix the message with the constructor name — `new TypeError('x')`
// has `message === 'x'`, not `'TypeError: x'`. Catch sites that re-throw
// often DO prefix; we cover that case via RAW_PREFIX_PATTERNS in
// looksLikeRawException.
const RAW_ERROR_NAMES = new Set([
  'TypeError',
  'ReferenceError',
  'SyntaxError',
  'RangeError',
  'URIError',
  'EvalError',
])

/**
 * Wraps an unknown error in a friendly fallback when it looks like raw
 * exception text; passes the original message through when it looks like
 * an already-plain backend response.
 *
 * @param err Anything caught — `Error`, string, undefined, JSON.
 * @param fallback The per-surface plain-language string to show when the
 *                 underlying message is unsafe to render directly.
 *                 e.g. "Couldn't load the run report. Try refreshing — if
 *                 the problem continues, check your connection."
 */
export function toUserMessage(err: unknown, fallback: string): string {
  // Built-in JS error subclasses → fallback regardless of their .message,
  // because the message itself ("Cannot read properties of undefined
  // (reading 'status')") reads as developer slop.
  if (err instanceof Error && RAW_ERROR_NAMES.has(err.name)) return fallback
  const raw = extractRawMessage(err)
  if (!raw) return fallback
  if (looksLikeRawException(raw)) return fallback
  return raw
}

/**
 * Returns the underlying raw message for "Copy details" — the unfriendly
 * text we hid behind a fallback. Operators paste this into support tickets
 * without it being the headline of the toast.
 *
 * Returns null when nothing useful is extractable (e.g. `err` is undefined),
 * so the caller can skip rendering a Copy details action.
 */
export function extractRawDetails(err: unknown): string | null {
  const raw = extractRawMessage(err)
  return raw && raw.trim().length > 0 ? raw : null
}

function extractRawMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (err === null || err === undefined) return ''
  try {
    return String(err)
  } catch {
    return ''
  }
}
